/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { useState } from 'react';
import { Button } from 'reactstrap';
import { ChartComponent } from './components/chart';
import UniversalTrackerModalService from '../../model/UniversalTrackerModalService';
import Dashboard, { DashboardRow } from '../dashboard';
import { canAddTarget } from '../../utils/universalTracker';
import { UtrvType } from '../../constants/status';
import DataTable from './components/data-table';
import { SetDataForm } from './components/SetDataForm';
import { UniversalTrackerPlain, UtrValueType } from '../../types/universalTracker';
import SimpleTooltip from '../simple-tooltip';
import { ShowAs } from '../../reducers/universal-tracker-modal';
import { AddToDashboardDropdown } from './AddToDashboardDropdown';
import { CollapsibleQuestionInfo } from './CollapsibleQuestionInfo';
import { DownloadUtrvHistoryButton } from './DownloadUtrvHistoryButton';
import { AdditionalInfo } from './AdditionalInfo';
import { ContentTabs, Tab } from './ContentTabs';
import { CurrentUserData } from '../../reducers/current-user';
import { ColumnFilter } from './ColumnFilter';
import { ViewRadioButtons } from './ViewRadioButtons';
import { useGetValueListByIdQuery } from '../../api/value-list';
import Loader from '../loader';
import { DISABLED_TARGET_BASELINE_ADDING_TOOLTIP } from '../../routes/custom-dashboard/utils';
import { QUESTION } from '../../constants/terminology';
import { SurveyFilters, SurveyFiltersProps } from './SurveyFilters';

type Props = Partial<SurveyFiltersProps> & {
  modalService: UniversalTrackerModalService;
  alternativeCode?: string;
  activeTabId?: Tab['navId'];
  canAccessCustomDashboards: boolean;
  hideProvenance?: boolean;
  reloadUtrvs: () => void;
  currentUser?: CurrentUserData;
  showTargetBaselineAdding: boolean;
  disableTargetBaselineAdding?: boolean;
}

function getErrorMessage(utr: Pick<UniversalTrackerPlain, 'valueType'>) {
  if (utr.valueType === UtrValueType.Table) {
    return `Currently targets/baselines are not supported for multi-row table ${QUESTION.PLURAL}`;
  }
  return 'Currently targets/baselines are not supported for this metric type';
}

export const UtrModalBody = (props: Props) => {
  const {
    modalService,
    alternativeCode,
    activeTabId,
    canAccessCustomDashboards,
    hideProvenance,
    reloadUtrvs,
    currentUser,
    showTargetBaselineAdding,
    disableTargetBaselineAdding = false,
    initialFilters,
    onChangeFilters,
  } = props;

  const [selectedColumnCode, setColumnCode] = useState<undefined | string>(undefined);

  const utr = modalService.getUniversalTracker();
  const utrvs = modalService.getUniversalTrackerValues();
  const utrPlain: UniversalTrackerPlain = utr.getRaw();
  const { data: valueList, isFetching } = useGetValueListByIdQuery(utrPlain.valueValidation?.valueList?.listId ?? '', {
    skip: !utrPlain.valueValidation?.valueList?.listId,
  });
  const canDrawChart = canAddTarget(utrPlain);

  const [activeTab, setActiveTab] = useState<Tab['navId']>(() => {
    return activeTabId ? activeTabId : canDrawChart ? ShowAs.Chart : ShowAs.Table;
  });

  const initiativeId = modalService.getInitiativeId() ?? '';

  const handleBack = () => setActiveTab(ShowAs.Table);

  if (isFetching) {
    return <Loader />
  }

  const viewTabs: Tab[] = [
    {
      navId: ShowAs.Chart,
      disabled: !canDrawChart,
      name: 'Chart',
      component: <ChartComponent utr={utr} utrvs={utrvs} selectedColumnCode={selectedColumnCode} />,
    },
    {
      navId: ShowAs.Table,
      name: 'Table',
      component: (
        <DashboardRow>
          <DataTable
            selectedColumnCode={selectedColumnCode}
            utr={utr}
            utrvs={utrvs}
            hideProvenance={hideProvenance}
            reloadUtrvs={reloadUtrvs}
            currentUser={currentUser}
            users={modalService.users}
            documents={modalService.documents}
          />
        </DashboardRow>
      ),
    },
  ];

  const getAddDataTabs = () => {
    if (!showTargetBaselineAdding) {
      return [];
    }

    const baselineTab = {
      navId: UtrvType.Baseline,
      name: 'Add a baseline',
      disabled: disableTargetBaselineAdding,
      component: (
        <SetDataForm
          utr={utr}
          utrvType={UtrvType.Baseline}
          initiativeId={initiativeId}
          userId={currentUser?._id}
          handleBack={handleBack}
          reloadUtrvs={reloadUtrvs}
        />
      ),
    };

    if (![UtrvType.Baseline, UtrvType.Target].includes(activeTab as UtrvType)) {
      return [{ ...baselineTab, name: 'Add a target/baseline' }];
    }

    return [
      baselineTab,
      {
        navId: UtrvType.Target,
        name: 'Add a target',
        disabled: disableTargetBaselineAdding,
        component: (
          <SetDataForm
            utr={utr}
            utrvType={UtrvType.Target}
            initiativeId={initiativeId}
            userId={currentUser?._id}
            handleBack={handleBack}
            reloadUtrvs={reloadUtrvs}
          />
        ),
      },
    ];
  };

  const addDataTabs: Tab[] = getAddDataTabs();

  const tabs = [...viewTabs, ...addDataTabs];

  const isAddDataMode = [UtrvType.Target, UtrvType.Baseline].includes(activeTab as UtrvType);

  return (
    <Dashboard className='utr-modal-container'>
      <CollapsibleQuestionInfo utr={utr} alternativeCode={alternativeCode} />

      <DashboardRow mb={0}>
        <div className='w-100 d-flex justify-content-between align-items-center' style={{ height: '38px' }}>
          {isAddDataMode ? (
            <Button color='link' onClick={handleBack}>
              <i className='fa fa-chevron-left mr-2' />
              Back to data history
            </Button>
          ) : (
            <>
              <ViewRadioButtons tabs={viewTabs} activeTab={activeTab} handleChangeTab={setActiveTab} />

              <ColumnFilter
                utr={utr}
                selectedColumnCode={selectedColumnCode}
                valueList={valueList}
                handleChangeColumn={setColumnCode}
              />
              
              {initialFilters && onChangeFilters ? (
                <SurveyFilters
                  key={`${initialFilters.surveyType}-${initialFilters.period}`}
                  initialFilters={initialFilters}
                  onChangeFilters={onChangeFilters}
                />
              ) : null}
            </>
          )}
          <div>
            {addDataTabs.map(({ navId, name, disabled }) => (
              <SimpleTooltip
                key={`btn-utrvType-${navId}`}
                text={
                  disabled
                    ? DISABLED_TARGET_BASELINE_ADDING_TOOLTIP
                    : canDrawChart
                    ? undefined
                    : getErrorMessage(utrPlain)
                }
              >
                <Button
                  disabled={!canDrawChart || disabled}
                  color={activeTab === navId ? 'primary' : 'secondary'}
                  active={activeTab === navId}
                  className='mr-2'
                  onClick={() => setActiveTab(navId)}
                >
                  {name}
                </Button>
              </SimpleTooltip>
            ))}
            <DownloadUtrvHistoryButton initiativeId={initiativeId} utrId={utr.getId()} />
          </div>
        </div>
      </DashboardRow>

      <ContentTabs tabs={tabs} activeTab={activeTab} />

      {isAddDataMode ? null : <AdditionalInfo utrPlain={utrPlain} />}

      {canAccessCustomDashboards ? (
        <div className='float-end ml-auto mt-auto'>
          <AddToDashboardDropdown
            initiativeId={initiativeId}
            utr={utr}
            selectedColumnCode={selectedColumnCode}
            alternativeCode={alternativeCode}
          />
        </div>
      ) : null}
    </Dashboard>
  );
};
